import { Card } from '../components/ui/cards'
import { MapPin, Satellite, Search, Zap } from 'lucide-react'

export const FoundAtEarthTwinExplainer = () => {
  return (
    <section className="relative py-20">
      <div className="absolute inset-0 z-10">
        <img
          src="img/sunrise.png"
          alt="Infrastructure worker using AR technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-home-primary/95 via-home-primary/80 to-home-primary/60"></div>
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:40px_40px] z-20"></div>
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 z-30">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            FoundAtEarth Twin Technology
          </h2>
          <p className="text-xl text-white font-normal max-w-3xl mx-auto">
            Revolutionary no-scan AR placement that eliminates the need for on-site. 
            Place infrastructure data remotely with precision.
          </p>
        </div>

        {/* Workflow Diagram */}
        <div className="max-w-5xl mx-auto mb-12">
          <Card className="p-8">
            <div className="mb-4 text-center">
              <h3 className="text-xl font-semibold mb-2">From Desktop to Field in 4 Steps</h3>
              <p className="text-sm text-home-muted-foreground">Complete workflow from office to on-site visualization</p>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-home-primary/5 rounded-lg border-2 border-home-primary">
                <div className="w-12 h-12 rounded-full bg-home-primary/20 flex items-center justify-center mx-auto mb-3 font-bold text-home-home-primary">
                  1
                </div>
                <h4 className="font-semibold mb-2">Desktop Placement</h4>
                <p className="text-sm text-muted-foreground">Admin drops geolocated pins on satellite imagery from any browser</p>
              </div>
              <div className="text-center p-4 bg-home-accent/5 rounded-lg border-2 border-home-primary">
                <div className="w-12 h-12 rounded-full bg-home-accent/20 flex items-center justify-center mx-auto mb-3 font-bold text-home-accent">
                  2
                </div>
                <h4 className="font-semibold mb-2">Instant Sync</h4>
                <p className="text-sm text-home-muted-foreground">Assets automatically sync to all authorized field devices in real-time</p>
              </div>
              <div className="text-center p-4 bg-home-primary/5 rounded-lg border-2 border-home-primary">
                <div className="w-12 h-12 rounded-full bg-home-primary/20 flex items-center justify-center mx-auto mb-3 font-bold text-home-primary">
                  3
                </div>
                <h4 className="font-semibold mb-2">Navigate to Site</h4>
                <p className="text-sm text-muted-foreground">Field worker travels to location (no GPS or pre-scanning required)</p>
              </div>
              <div className="text-center p-4 bg-home-accent/5 rounded-lg border-2 border-home-primary">
                <div className="w-12 h-12 rounded-full bg-home-accent/20 flex items-center justify-center mx-auto mb-3 font-bold text-accent">
                  4
                </div>
                <h4 className="font-semibold mb-2">AR Visualization</h4>
                <p className="text-sm text-home-muted-foreground">Worker sees precise AR overlay of infrastructure on arrival</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Key Capabilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <Card className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-home-primary">
            <div className="mb-4">
              <div className="w-12 h-12 rounded-full bg-home-primary/10 flex items-center justify-center mb-3">
                <MapPin className="w-6 h-6 text-home-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Remote Placement</h3>
            </div>
            <p className="text-sm text-home-muted-foreground">
              Drop pins from anywhere—office, home, or vehicle. No need to visit the site first.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-home-accent">
            <div className="mb-4">
              <div className="w-12 h-12 rounded-full bg-home-accent/10 flex items-center justify-center mb-3">
                <Zap className="w-6 h-6 text-home-accent" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Zero Scanning</h3>
            </div>
            <p className="text-sm text-home-muted-foreground">
              Bypass traditional mapping and scanning. Assets appear instantly in AR without site preparation.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-home-primary">
            <div className="mb-4">
              <div className="w-12 h-12 rounded-full bg-home-primary/10 flex items-center justify-center mb-3">
                <Satellite className="w-6 h-6 text-home-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">GPS-Free Precision</h3>
            </div>
            <p className="text-sm text-home-muted-foreground">
              Works indoors, underground, and in GPS-denied environments with high accuracy.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-home-accent">
            <div className="mb-4">
              <div className="w-12 h-12 rounded-full bg-home-accent/10 flex items-center justify-center mb-3">
                <Search className="w-6 h-6 text-home-accent" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Region Search</h3>
            </div>
            <p className="text-sm text-home-muted-foreground">
              Automatically discover and load all infrastructure within your search radius or geofenced zone.
            </p>
          </Card>
        </div>

        {/* Technical Advantage */}
        <div className="mt-12 max-w-4xl mx-auto">
          <Card className="p-8 bg-gradient-to-r from-home-primary/10 via-home-accent/10 to-home-primary/10">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Why This Matters</h3>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div>
                  <div className="font-semibold text-home-primary mb-2">80% Faster Deployment</div>
                  <p className="text-sm text-home-muted-foreground">
                    No mapping crews, no scanning time—assets are ready immediately
                  </p>
                </div>
                <div>
                  <div className="font-semibold text-home-accent mb-2">Works Anywhere</div>
                  <p className="text-sm text-home-muted-foreground">
                    Basements, tunnels, dense urban areas—GPS not required
                  </p>
                </div>
                <div>
                  <div className="font-semibold text-home-primary mb-2">Scale Globally</div>
                  <p className="text-sm text-home-muted-foreground">
                    Deploy across thousands of sites without onsite prep
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
